import React from 'react';
import { useNavigate } from 'react-router-dom';
import './FloatingActionButton.css';

interface FloatingActionButtonProps {
  /** Route to navigate to when clicked */
  to: string;
  /** Icon to display inside the button */
  icon?: React.ReactNode;
  /** ARIA label for accessibility */
  ariaLabel?: string;
  /** Test ID for testing */
  testID?: string;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Click handler (if you want custom behavior instead of navigation) */
  onClick?: () => void;
  /** Position from bottom (defaults to 90px for iOS tab bar clearance) */
  bottom?: number;
  /** Position from right (defaults to 20px) */
  right?: number;
}

/**
 * Floating Action Button (FAB) Component
 *
 * A circular floating action button that follows iOS design patterns.
 * Uses DinnerBrain's coral color scheme and provides smooth animations.
 * Optimized for touch targets (56px minimum for accessibility).
 */
export const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  to,
  icon,
  ariaLabel = 'Add new item',
  testID = 'floating-action-button',
  disabled = false,
  onClick,
  bottom = 90,
  right = 20
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (disabled) return;

    if (onClick) {
      onClick();
    } else {
      navigate(to);
    }
  };

  const defaultIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  );

  const dynamicStyle: React.CSSProperties = {
    bottom: `${bottom}px`,
    right: `${right}px`
  };

  return (
    <button
      className={`floating-action-button ${disabled ? 'disabled' : ''}`}
      onClick={handleClick}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={testID}
      style={dynamicStyle}
      type="button"
    >
      <span className="fab-icon" aria-hidden="true">
        {icon || defaultIcon}
      </span>
    </button>
  );
};

export default FloatingActionButton;